import { AuthenticationServiceBase } from '@wz-services';
import { isAfter, isBefore, isEqual } from 'date-fns';
import { AuthenticationModels } from 'models/authentication.models';
import { AvenantModels } from 'models/avenant.models';
import { ClientModels } from 'models/client.models';
import { CommonModels } from 'models/common.models';
import { ErrorModels } from 'models/error.model';
import { HealthModels } from 'models/health.models';
import { PolicyModels } from 'models/policy.models';
import { RequestModels } from 'models/request.models';
import { RegexConstants } from '../constants/regex-constants';
import { ResourcesService } from '../modules/tunnelsante/services/resources/resources.service';
import { getDateUtc } from './date.utilities';

export const avenantStateAllowCreation = [
  RequestModels.RequestStateEnum.Cancelled,
  RequestModels.RequestStateEnum.Handled,
  RequestModels.RequestStateEnum.Rejected,
];

export const terminationStateAllowCreation = [
  RequestModels.RequestStateEnum.Cancelled,
  RequestModels.RequestStateEnum.Rejected,
];

export const cancelledPolicyStates = [
  PolicyModels.PolicyStateEnum.Resilie,
  PolicyModels.PolicyStateEnum.Caducite,
  PolicyModels.PolicyStateEnum.Nullite,
  PolicyModels.PolicyStateEnum.FinDeGarantieProvisoire,
];

export function formatPublicId(value: string) {
  return value.replace(RegexConstants.WzPublicId, '$1 $2 $3 $4$5');
}

export function prettyFullName(
  resService: ResourcesService,
  person: {
    civility?: CommonModels.CivilityEnum;
    firstName: string;
    lastName: string;
  },
): string {
  if (!person) {
    return '';
  }

  if (person.firstName && person.lastName) {
    if (person.civility) {
      const civility = resService.getResourceEnum(
        person.civility,
        'CIVILITY_SHORT_ENUM',
      );
      return `${civility} ${person.lastName} ${person.firstName}`;
    } else {
      return `${person.lastName} ${person.firstName}`;
    }
  }

  return '';
}

export function canChangeQuantieme(
  authService: AuthenticationServiceBase<any, any>,
  state: PolicyModels.PolicyStateEnum,
  avenantRequest: AvenantModels.AvenantRequest,
  terminationRequest: RequestModels.TerminationRequest,
) {
  return (
    (!terminationRequest ||
      terminationStateAllowCreation.indexOf(terminationRequest.state) !== -1) &&
    (!avenantRequest ||
      avenantStateAllowCreation.indexOf(avenantRequest.state) !== -1) &&
    authService.hasRight(
      AuthenticationModels.RightEnum.UpdatePolicyQuantieme,
    ) &&
    state === PolicyModels.PolicyStateEnum.EnCours
  );
}

export function canAskTermination(
  authService: AuthenticationServiceBase<any, any>,
  state: PolicyModels.PolicyStateEnum,
  terminationRequest: RequestModels.TerminationRequest,
  avenantRequest: AvenantModels.AvenantRequest,
) {
  return (
    (!terminationRequest ||
      terminationStateAllowCreation.indexOf(terminationRequest.state) !== -1) &&
    (!avenantRequest ||
      avenantStateAllowCreation.indexOf(avenantRequest.state) !== -1) &&
    authService.hasRight(
      AuthenticationModels.RightEnum.CreateTerminationRequest,
    ) &&
    (state === PolicyModels.PolicyStateEnum.EnCours ||
      state === PolicyModels.PolicyStateEnum.MisEnDemeure ||
      state === PolicyModels.PolicyStateEnum.ActifAvecRejet)
  );
}

export function canAskAvenant(
  authService: AuthenticationServiceBase<any, any>,
  state: PolicyModels.PolicyStateEnum,
  policyEffectDate: Date,
  avenantRequest: AvenantModels.AvenantRequest,
  terminationRequest: RequestModels.TerminationRequest,
  hasRenewal: boolean,
) {
  return (
    (!terminationRequest ||
      terminationStateAllowCreation.indexOf(terminationRequest.state) !== -1) &&
    (!avenantRequest ||
      avenantStateAllowCreation.indexOf(avenantRequest.state) !== -1) &&
    authService.hasRight(AuthenticationModels.RightEnum.CreateAvenantRequest) &&
    state === PolicyModels.PolicyStateEnum.EnCours &&
    (isEqual(policyEffectDate, getDateUtc() ?? new Date()) ||
      isBefore(policyEffectDate, getDateUtc() ?? new Date()))
  );
}

export function canPayReceipts(
  avenantRequest: AvenantModels.AvenantRequest,
  state: PolicyModels.PolicyStateEnum,
) {
  return (
    (!avenantRequest ||
      avenantStateAllowCreation.indexOf(avenantRequest.state) !== -1) &&
    cancelledPolicyStates.indexOf(state) === -1
  );
}

export function prettyCar(car: PolicyModels.VehicleDetails, codeGta?: string) {
  if (!car) {
    return '';
  }

  let vehicle = `${car.marque} ${car.modele} (${car.chevauxFiscaux}CV) ${car.energie} ${car.version}`;
  if (codeGta) {
    vehicle += ` (${codeGta})`;
  }
  return vehicle;
}

export const policyIsInStates = (
  cp: PolicyModels.PolicyRecap,
  states: PolicyModels.PolicyStateEnum[],
) => cp && states.indexOf(cp.status) !== -1;

export const policyIsNotInStates = (
  cp: PolicyModels.PolicyRecap,
  states: PolicyModels.PolicyStateEnum[],
) => !policyIsInStates(cp, states);

export const policyIsNotGarantieProvisoireNorCancelled = (
  cp: PolicyModels.PolicyRecap,
) => {
  return (
    cp &&
    policyIsNotInStates(cp, [
      PolicyModels.PolicyStateEnum.GarantieProvisoire,
      PolicyModels.PolicyStateEnum.FinDeGarantieProvisoire,
      PolicyModels.PolicyStateEnum.Traitement,
      PolicyModels.PolicyStateEnum.Caducite,
      PolicyModels.PolicyStateEnum.Nullite,
    ])
  );
};

export const policyIsOkAvenant = (cp: PolicyModels.PolicyRecap) => {
  return (
    cp &&
    policyIsNotInStates(cp, [
      PolicyModels.PolicyStateEnum.GarantieProvisoire,
      PolicyModels.PolicyStateEnum.FinDeGarantieProvisoire,
      PolicyModels.PolicyStateEnum.Traitement,
      PolicyModels.PolicyStateEnum.Caducite,
      PolicyModels.PolicyStateEnum.Nullite,
      PolicyModels.PolicyStateEnum.ActifAvecRejet,
      PolicyModels.PolicyStateEnum.MisEnDemeure,
      PolicyModels.PolicyStateEnum.Suspendue,
      PolicyModels.PolicyStateEnum.SansEffet,
      PolicyModels.PolicyStateEnum.Resilie,
    ])
  );
};

export const policyIsGarantieProvisoire = (cp: PolicyModels.PolicyRecap) =>
  cp &&
  policyIsInStates(cp, [
    PolicyModels.PolicyStateEnum.GarantieProvisoire,
    PolicyModels.PolicyStateEnum.Traitement,
  ]);

export const computeStringError = (
  obj: string | ErrorModels.TunnelSanteError,
) => {
  if (typeof obj === 'string') {
    return obj;
  }

  return obj.appMessage;
};
export const mapWithholdingToLinear = (
  withholding: HealthModels.HealthRateWithholdingEnum,
): HealthModels.HealthRateLinearEnum => {
  switch (withholding) {
    case HealthModels.HealthRateWithholdingEnum.PR1:
      return HealthModels.HealthRateLinearEnum.L1;
    case HealthModels.HealthRateWithholdingEnum.PR2:
      return HealthModels.HealthRateLinearEnum.L2;
    case HealthModels.HealthRateWithholdingEnum.PR3:
      return HealthModels.HealthRateLinearEnum.L3;
    case HealthModels.HealthRateWithholdingEnum.PR4:
      return HealthModels.HealthRateLinearEnum.L4;
    case HealthModels.HealthRateWithholdingEnum.PR5:
      return HealthModels.HealthRateLinearEnum.L5;
    case HealthModels.HealthRateWithholdingEnum.PR6:
      return HealthModels.HealthRateLinearEnum.L6;
    case HealthModels.HealthRateWithholdingEnum.PR7:
      return HealthModels.HealthRateLinearEnum.L7;
    default:
      throw new Error('Unknown HealthRateWithholdingEnum value');
  }
};

export const isAllowedToPayUnpaidReceipts = (
  policy: PolicyModels.Policy,
  client: ClientModels.Client,
  unpaidReceipts: PolicyModels.UnpaidReceipts,
) => {
  let dateNow = new Date();
  return (
    client &&
    policy &&
    unpaidReceipts &&
    (policy.termination === null ||
      (policy.termination &&
        policy.termination.paymentLimitDate &&
        isAfter(policy.termination.paymentLimitDate, dateNow)))
  );
};
